import {
  Box,
  DialogTitle,
  Divider,
  List,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { FC, useCallback } from 'react';

import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import { EmptyState } from '@/components/navbar/notifications/notification-empty-state';
import { FormattedMessage } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroll-component';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useDialog } from '@/logic/state';
import { useNotifications } from '@/hooks/use-notifications';
import NotificationItem from './notification-item';
import {
  NotificationTypes,
  useMakeNotificationDeleted,
} from '@/logic/services/notifications';
import { useUnreadNotifications } from '@/logic/state/unread-notifications';
import BillPrimaryIcon from '/public/icons/notifications/notification-bill-primary-icon.svg';
import OfferIcon from '/public/icons/notifications/offer-yellow-icon.svg';
import TicketSecondaryIcon from '/public/icons/notifications/ticket-secondary-icon.svg';
import WalletNotificationIcon from '/public/icons/notifications/wallet-notification-icon.svg';
import { BackwardButton } from '@/components/shared/backward-button';

interface NotificationsPopoverContentProps {
  handleCloseNotifications?: () => void;
}

const NotificationsPopoverContent: FC<NotificationsPopoverContentProps> = ({
  handleCloseNotifications,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1300));
  const [setUnreadNotifications] = useUnreadNotifications(
    s => [s.setUnreadNotifications],
    shallow
  );
  const setDialog = useDialog(s => s.setDialog, shallow);

  const handleBackwardClick = useCallback(() => {
    setDialog({
      wrapper: false,
      isNotificationPopover: false,
    });
  }, [setDialog]);
  const handleClose = () =>
    isSmallScreen ? handleBackwardClick() : handleCloseNotifications();
  const {
    notifications,
    isFetching,
    fetchNextPage,
    hasNextPage,
    calculateNotificationTime,
    refetch,
    handleNotificationClick,
  } = useNotifications({ handleClose });

  const { mutate: deleteNotification } = useMakeNotificationDeleted(
    {},
    {
      onSuccess: data => {
        refetch();
        setUnreadNotifications(data.count);
      },
    }
  );

  const handleNotificationImage = useCallback((type: NotificationTypes) => {
    if (
      type === NotificationTypes.WELCOME ||
      type === NotificationTypes.GENERAL ||
      type === NotificationTypes.EXTERNAL_LINK
    ) {
      return BillPrimaryIcon;
    } else if (type === NotificationTypes.OFFER) {
      return OfferIcon;
    } else if (
      type === NotificationTypes.FILM ||
      type === NotificationTypes.UPCOMING_FILM ||
      type === NotificationTypes.ORDER_REMINDER
    ) {
      return TicketSecondaryIcon;
    } else if (
      type === NotificationTypes.EXPIRED_EWALLET_TRANSACTION ||
      type === NotificationTypes.EXPIRED_CASHBACK_WALLET_TRANSACTION ||
      type === NotificationTypes.REFUND_TRANSACTION ||
      type === NotificationTypes.TOP_UP_TRANSACTION
    ) {
      return WalletNotificationIcon;
    }
  }, []);

  const onDeleteNotification = useCallback(
    (id: string) => {
      deleteNotification({
        query: {
          id,
        },
      });
    },
    [deleteNotification]
  );

  return (
    <>
      {isSmallScreen && (
        <DialogTitle>
          <BackwardButton handleClick={() => handleBackwardClick()} />
        </DialogTitle>
      )}
      {(!notifications || notifications.length === 0) && !isFetching ? (
        <EmptyState />
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: {
                  mobile: '18px',
                  tablet: pxToRem(18),
                },
                color: theme.palette.grey[80],
                lineHeight: '27px',
                marginInlineStart: pxToRem(16),
                marginBlockStart: pxToRem(24),
              }}
            >
              <FormattedMessage id='notifications' />
            </Typography>
            {isFetching && (
              <CustomizedCircularProgress
                size={20}
                thickness={4}
                customSx={{ marginInlineEnd: '20px' }}
              />
            )}
          </Box>
          <List
            id='scrollable-notifications'
            sx={{
              maxHeight: {
                mobile: '90vh',
                tablet: '300px',
              },
              overflow: 'auto',
            }}
          >
            <InfiniteScroll
              dataLength={notifications.length}
              next={fetchNextPage}
              hasMore={!!hasNextPage}
              loader={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CustomizedCircularProgress size={40} thickness={4} />
                </Box>
              }
              style={{
                overflow: 'unset',
              }}
              scrollableTarget={'scrollable-notifications'}
            >
              {notifications?.map((notification, index: number) => (
                <Box key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    handleNotificationClick={handleNotificationClick}
                    handleNotificationImage={handleNotificationImage}
                    calculateNotificationTime={calculateNotificationTime}
                    onDeleteNotification={onDeleteNotification}
                  />
                  {index !== notifications?.length - 1 && (
                    <Divider sx={{ marginInline: pxToRem(16) }} />
                  )}
                </Box>
              ))}
            </InfiniteScroll>
          </List>
        </>
      )}
    </>
  );
};

export default NotificationsPopoverContent;
